import React from 'react';
import { AnyAction, Reducer as ReduxReducer } from 'redux';

import {
  LocalizationValue,
  LocalizationsFront,
  NOT_APPLICABLE,
  FormatValue,
  FormatsFront,
} from '../creativeAnalyzer/model';
import { DatatypeForType, Type } from './modules/filter/dataModel';
import { z } from 'zod';
export type BLTeam = { id: number; name: string };
export const NO_TEAM = -1;
export const NO_TEAM_string = '<no team>';
/**
 * @deprecated
 */
export interface ActionWithPayload<P = any, T = string> extends AnyAction {
  payload: P;
  type: T;
}
export type ReducerWithPayload<S, P = any> = ReduxReducer<S, ActionWithPayload<P>>;
/**
 * Key-Value pairs
 */

export type Message = (...args: any[]) => {
  title: string;
  message?: string;
};

export interface MessagesInterface extends Record<string, Message> {}

export interface Project {
  id: number;
  name: string;
  short_name: string;
  studio: number;
  apple_store_url: string | null;
  google_store_url: string | null;
  is_autotesting_on: boolean;
}

export type CaFbProject = {
  group_id: number;
  project_title: string;
  short_name: string | null;
  //используем crhub_project_id как *главный* id
  crhub_project_id: number;
};
export type CaFbProjectWithAutotest = CaFbProject & {
  is_autotesting_on: boolean;
};

export type ProjectLocalization = Omit<ProjectLocalizationBack, 'localization' | 'formats'> & {
  localization: LocalizationsFront;
  formats: FormatsFront;
};
export type ProjectLocalizationSocialKey =
  | 'youtube'
  | 'unity'
  | 'google'
  | 'tiktok'
  | 'facebook'
  | 'applovin'
  | 'mintegral';

// this is fine as far as TS is concerned
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const ProjectLocalizationSocialKey: ProjectLocalizationSocialKey[] = [
  'unity',
  'facebook',
  'google',
  'youtube',
  'tiktok',
  'applovin',
  'mintegral',
];
export type ProjectLocalizationBack = {
  name: Project['name'];
  localization: LocalizationValue[] | null;
  formats: FormatValue[] | null;
  // social: Record<ProjectLocalizationSocialKey, 'uploaded' | 'launched' | null>;
  created: string;
  updated: string;
  id: Project['id'];
  node_id: number;
  files_count: Record<string, number> | null;
};

export function convert2front(row: ProjectLocalizationBack): ProjectLocalization {
  return {
    ...row,
    localization: row.localization ?? NOT_APPLICABLE,
    formats: row.formats ?? NOT_APPLICABLE,
  };
}

export enum CreativeStatus {
  new = 0,
  active = 10,
  rated = 20,
}

export interface CreativeType {
  id: number;
  name: string;
  example?: string;
}

export enum RatingValue {
  BAD = 'BAD',
  NORMAL = 'NORMAL',
  GOOD = 'GOOD',
  UNRATED = 'UNRATED',
}

// один из них тип, второй — константа
export type VALID_RATINGS = RatingValue.GOOD | RatingValue.NORMAL | RatingValue.BAD;

export const RatingInfo: Record<RatingValue, { text: string; value: number | null }> = {
  [RatingValue.GOOD]: { text: 'Good', value: 30 },
  [RatingValue.NORMAL]: { text: 'Normal', value: 20 },
  [RatingValue.BAD]: { text: 'Bad', value: 10 },
  [RatingValue.UNRATED]: { text: 'Unrated', value: null },
};

const RATED_RATING_OPTIONS = [RatingValue.GOOD, RatingValue.NORMAL, RatingValue.BAD];

export const ALL_RATING_OPTIONS = [...RATED_RATING_OPTIONS, RatingValue.UNRATED];

export const RATING_DROPDOWN_OPTIONS: {
  text: string;
  value: RatingValue;
}[] = RATED_RATING_OPTIONS.map((v) => ({
  text: RatingInfo[v].text,
  value: v,
}));

export enum Permission {
  'fs.assetlab_access' = 'fs.assetlab_access',
  'fs.assetlab_favorites' = 'fs.assetlab_favorites',
  'fs.assetlab_packs' = 'fs.assetlab_packs',
  'fs.assetlab_create_public_sharing_link' = 'fs.assetlab_create_public_sharing_link',
  'fs.assetlab_create_safety_sharing_link' = 'fs.assetlab_create_safety_sharing_link',
  'fs.assetlab_set_tag_to_asset' = 'fs.assetlab_set_tag_to_asset',
  'fs.assetlab_create_tag' = 'fs.assetlab_create_tag',
  'fs.assetlab_asset_links' = 'fs.assetlab_asset_links',
  'fs.crhub_admin_access' = 'fs.crhub_admin_access',
  'fs.add_sharing' = 'fs.add_sharing',
  'bl.force_can_edit_creative' = 'bl.force_can_edit_creative',
  'fs.assetlab_manage_files' = 'fs.assetlab_manage_files',
  // Право юриста на модерацию лицензий загруженных файлов (шрифтов). MYUA-16456.
  'laws.moderate_licenses' = 'laws.moderate_licenses',
}

// --- Font license moderation (MYUA-16456) ---
// Доменная модель лицензии шрифта. Соответствует объекту `font_license`, который бэкенд
// (MYUA-16444) возвращает в деталях ассета (GET /api/asset-lab/assets/{id}).
export enum LicenseStatus {
  UnderModeration = 'UNDER_MODERATION',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
}

export type FontLicenseType = { id: number; name: string };

export type FontLicenseProject = { id: number; name: string };

export type FontLicense = {
  id: number;
  license_type: FontLicenseType | null;
  status: LicenseStatus;
  projects: FontLicenseProject[] | null; // null = Unlimited
  expiration_date: string | null; // null = Unlimited
  // Шрифт внутри архива, к которому относится лицензия (`fonts/Arial.ttf`), либо null,
  // если лицензия на сам файл-шрифт. У архива со шрифтами лицензий несколько — по одной
  // на шрифт, и различает их именно это поле. Поле может отсутствовать в старых ответах.
  filename?: string | null;
};

// Текст тултипа для кнопок шаринга/Google Drive, задизейбленных на демо-стенде (MYUA-16201).
export const DEMO_UNAVAILABLE_TOOLTIP = 'This feature is unavailable in demo mode';

export enum UserRole {
  crhub_admin = 'crhub_admin',
  superusers = 'superusers',
  creative_manager = 'creative_manager',
  creative_testing_managers = 'creative_testing_managers',
  creative_ua_manager = 'creative_ua_manager',
  crhub_outsource = 'crhub_outsource',
}

// это то, что в юзере есть всегда
// и в /oauth/user и в /crhub/mg/users
export interface AnyUser {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  username: string;
  last_login: string | null;
  //groups: string[];
  team: BLTeam | null;
  is_active: boolean;
  user_access_state_model_name: string | null;
}
// а это только в /oauth/user
export interface CurrentUser extends AnyUser {
  permissions: Permission[];
  exp_time: string | null;
  is_superuser: boolean;
}

export type UserForAdminister = {
  id: number;
  date_joined: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  last_login: string | null;
  team: { id: number; name: string } | null;
  username: string;
};

// user methods start
export function userToString(user: AnyUser | UserForAdminister) {
  return `${user.username} ${userFullName(user).length > 0 ? ', ' + userFullName(user) : ''}`;
}
export function userFullName(user: AnyUser | UserForAdminister) {
  return [user.first_name, user.last_name].join(' ');
}
export function userFullName2(user: { first_name: React.ReactNode; last_name: React.ReactNode }) {
  return React.createElement(React.Fragment, {}, user.first_name, ' ', user.last_name);
}
// user methods end

// export function userInGroup(user: AnyUser, group: UserRole) {
//   return user.groups.includes(group);
// }

// beware of subtypes: https://stackoverflow.com/a/55012175
export function typedObjectKeys<T extends object>(o: T): (keyof T)[] {
  return Object.keys(o) as (keyof T)[];
}

export type ID = number | string | null;

export type ManagerUser = {
  email: string;
  first_name: string;
  last_name: string;
  id: ID;
};

export const DATE_FORMAT = 'YYYY-MM-DD';

export enum UserAccessRight {
  read = 'read',
  write = 'write',
  delete = 'delete',
  admin = 'admin',
}

// --- Project owner feature (MYUA-16204) ---
// shape of UserShortSerializer on the backend
export type OwnerUser = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  username: string;
  email: string;
  is_active: boolean;
  team: BLTeam | null;
};

// subset of NodeSerializer fields used by the projects list
export type ProjectNode = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  flags: string[];
  abs_path: string;
};

// item of GET /api/crhub/bl/project-owner
export type ProjectOwnerRow = {
  node: ProjectNode;
  owner: OwnerUser | null;
};

// item of GET /api/crhub/fs/permissions/bulk/<node> (AccessRecord + owning user)
export type BulkAccessItem = {
  id: number;
  node_id: number;
  perms: string[];
  abs_path: string;
  user: OwnerUser;
};

export const ownerFullName = (user: Pick<OwnerUser, 'first_name' | 'last_name'>) =>
  [user.first_name, user.last_name].filter(Boolean).join(' ');

export const ASSET_LAB_PERMISSION = {
  read: 'Read',
  download: 'Download/Share',
} as const;

export type AssetLabPermission = keyof typeof ASSET_LAB_PERMISSION;

/**
 * Описание колонки таблицы файлов.
 *
 * Это НЕ колонка какой-то конкретной таблицы: поверх раскладки тут висят
 * `filter`, `plugins`, `getter`, `toDisplayString` — их потребляет система
 * фильтров и сортировки (`general/modules/filter`, `fileManager/selectors`),
 * минуя саму таблицу. Поэтому описание своё, а в колонки kit-таблицы оно
 * переводится адаптером (`fileManager/toAntdColumns.tsx`).
 */
export interface BaseTableColumn<
  // тип данных ячейки этой колонки
  Datatype extends Type,
  // набор ключей колонки
  ColumnNamespace extends React.Key = React.Key,
  // тип данных строки таблицы
  RowType = any,
> {
  key: ColumnNamespace;
  dataKey: string;
  title?: React.ReactNode;
  /** Фиксированная ширина колонки. */
  width?: number;
  /** Тянущаяся колонка: занимает свободное место, но не уже этого значения. */
  minWidth?: number;
  /**
   * Закрепить колонку у края при горизонтальной прокрутке. Закреплённые
   * колонки должны идти подряд от своего края — иначе antd оставит между ними
   * дырку из прокручиваемых.
   */
  fixed?: 'left' | 'right';
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
  /** Содержимое ячейки. Строку вернуть тоже можно — не только элемент. */
  cellRenderer?: (props: any) => React.ReactNode;
  /** Заголовок-компонент вместо текста (чекбокс «выбрать все» и подобное). */
  headerRenderer?: React.FC<any>;
  /** Обрезать содержимое многоточием вместо переноса. */
  ellipsis?: boolean;
  plugins?: React.FC<{ column: BaseTableColumn<Datatype, ColumnNamespace, RowType> }>[];
  filter?: Datatype;
  // значение для сортировки, а так же ключ фильтра
  getter: (node: RowType) => DatatypeForType<Datatype> | null;
  // Необязательный «множественный» геттер для колонок, у которых в строке
  // может быть массив значений (например, несколько жанров). Если задан,
  // клиентская фильтрация использует пересечение этого массива с условием
  // enum, а не одиночное значение из `getter`.
  multiGetter?: (node: RowType) => Array<DatatypeForType<Datatype>>;
  // Варианты enum-фильтра для колонки с `multiGetter`: пары «значение —
  // подпись» по каждому элементу массива (например, по каждому тегу).
  // Без него `FilterEnumContainer` собирает варианты из скалярного `getter`,
  // и строка с несколькими значениями дала бы один вариант на всю комбинацию.
  multiOptions?: (node: RowType) => Array<{ id: DatatypeForType<Datatype>; title: string }>;
  toDisplayString: (node: RowType) => string | null;
}

export enum NodeType {
  file = 'file',
  folder = 'folder',
}

export enum NodeFlag {
  animation_search = 'animation_search',
  folder_creative = 'folder_creative',
  in_test = 'in_test',

  folder_type = 'folder_type',
  folder_platform = 'folder_platform',
  folder_target = 'folder_target',
  folder_function = 'folder_function',
  folder_project = 'folder_project',
  folder_creative_type = 'folder_creative_type',

  assetlab = 'assetlab',
  can_change_only_admin = 'can_change_only_admin',
  figma_file = 'figma_file',
}

export interface HasFlags {
  flags: string[];
}

const Tag = z.object({
  id: z.number(),
  name: z.string(),
});

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Tag = z.infer<typeof Tag>;

export const INode = z.object({
  id: z.number(),
  name: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  type: z.string(), // type: NodeType;
  flags: z.array(z.string()), // flags: NodeFlag[];
  tags: z.array(Tag), // tags: Tag[];
  perms: z.array(z.string()).optional(), // perms: UserAccessRight[];
  download_url: z.string().nullable(),
  can_download_direct: z.boolean(),
  direct_source: z.string().nullable(),
  preview_url: z.string().nullable(),
  size: z.number().nullable(),
  is_linked: z.boolean().optional().default(false),
  // Флаг модерации: для файлов под модерацией/отклонённых бэкенд присылает false.
  // Отсутствие поля трактуем как true (см. isNodeDownloadAllowed, MYUA-16456).
  downloading_allowed: z.boolean().optional(),
  // AI-разметка сюжета: true для ноды-креатива с успешным распознаванием (MYUA-16423).
  // Приходит только из /fs/list_folder, в текстовом поиске отсутствует — трактуем как false.
  has_plot_recognition: z.boolean().optional(),
  // Разметка сюжета в выдаче семантического поиска (MYUA-16771): при `search_type=semantic`
  // бэкенд кладёт её прямо в ноду, а `has_plot_recognition` не отдаёт. Схема должна знать о
  // поле, иначе zod вырежет его при разборе ответа и иконка разметки на /search пропадёт.
  // Содержимое не описываем — фронт пока читает только факт наличия (CHNodeHasPlotRecognition).
  plot_recognition: z.record(z.unknown()).nullable().optional(),
  // Статус AI-разметки сюжета для столбца `Recognition` (MYUA-16736): pending/processing/
  // success/error. Как и has_plot_recognition, приходит только из /fs/list_folder для нод
  // внутри папки с флагом creative_type; отсутствие/null = разметки не было.
  plot_recognition_status: z.string().nullable().optional(),
});

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type INode = z.infer<typeof INode>;

export const CORE_DIRECTORY_FLAGS = [
  NodeFlag.folder_type,
  NodeFlag.folder_platform,
  NodeFlag.folder_target,
  NodeFlag.folder_function,
  NodeFlag.folder_project,
  NodeFlag.folder_creative_type,
];

export type MisleadLevel = { name: string; id: number; is_unset?: boolean };

export type ConceptType = { id: number; name: string };

export const cssVarKey = 'css-var-general';

export type EventLog = {
  id: number;
  creative_id: number;
  user: {
    id: number;
    email: string;
  };
  created_at: string;
  event_type: 'file_added' | 'file_removed';
  file_name: string;
};

export type VideoMergeConfig = {
  id: number;
  name: string;
  current_tree_folder: VideoMergeNode;
  packshot_folder: VideoMergeNode;
  packshot_nodes: VideoMergeNode[];
  transition_folder: VideoMergeNode;
  transition_nodes: VideoMergeNode[];
  screenshot_folder: VideoMergeNode;
};
type VideoMergeNode = { id: number; name: string };
